'use client';

import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { useUser } from '@clerk/clerk-react';

import {
  countUnread,
  createInboxClient,
  fetchInboxSnapshot,
  markAllRead,
  markMessageRead,
} from '@/lib/courier-inbox';
import type { InboxSnapshot } from '@/lib/courier-inbox';
import type { InboxNotification } from '@/types/notifications';
import { useNotificationToken } from './useNotificationToken';

// Deliberately polling, no websocket in v1 — a minute of latency is invisible next to
// the pipeline's aggregation window.
const INBOX_REFRESH_MS = 60 * 1000;

export interface NotificationInbox {
  /** False until the environment proves notifications configured — hide the bell. */
  enabled: boolean;
  notifications: InboxNotification[];
  unreadCount: number;
  isLoading: boolean;
  markRead: (messageId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

export function useNotificationInbox(): NotificationInbox {
  const { user } = useUser();
  const userId = user?.id ?? null;
  const { enabled, jwt, isLoading: tokenLoading } = useNotificationToken();

  const client = useMemo(
    () => (userId && jwt ? createInboxClient(userId, jwt) : null),
    [userId, jwt],
  );

  const inboxQuery = useSWR<InboxSnapshot>(
    client ? ['notifications-inbox', userId] : null,
    () => fetchInboxSnapshot(client!),
    {
      refreshInterval: INBOX_REFRESH_MS,
      // Browser notifications are emitted while this page is alive in a background
      // tab, so the inbox must keep observing arrivals after visibility changes.
      refreshWhenHidden: true,
      revalidateOnFocus: true,
    },
  );

  const notifications = useMemo(
    () => inboxQuery.data?.notifications ?? [],
    [inboxQuery.data],
  );

  const markRead = useCallback(
    async (messageId: string) => {
      if (!client) return;
      await markMessageRead(client, messageId);
      void inboxQuery.mutate(
        (current) =>
          current && {
            notifications: current.notifications.map((n) =>
              n.messageId === messageId ? { ...n, read: true } : n,
            ),
            unreadCount: Math.max(0, current.unreadCount - 1),
          },
        { revalidate: false },
      );
    },
    [client, inboxQuery],
  );

  const markAllAsRead = useCallback(async () => {
    if (!client) return;
    await markAllRead(client);
    void inboxQuery.mutate(
      (current) =>
        current && {
          notifications: current.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        },
      { revalidate: false },
    );
  }, [client, inboxQuery]);

  return {
    enabled,
    notifications,
    // Vendor total when the snapshot has one; derived count is the fallback inside the
    // snapshot mapping itself.
    unreadCount: inboxQuery.data?.unreadCount ?? countUnread(notifications),
    isLoading: tokenLoading || inboxQuery.isLoading,
    markRead,
    markAllAsRead,
  };
}
